import { getApiParams } from 'api';
import { selectPageModelCategoryId, selectPageModelRegionId } from 'store/pageModel';
import { all, call, put, select, takeLatest } from 'typed-redux-saga';
import {
	fetchBestSellersData,
	fetchConfigurators,
	fetchConfiguratorSiblingsData,
	fetchRating,
} from './api';
import { selectConfiguratorsIds } from './selectors';
import {
	fetchConfiguratorsFailed,
	fetchConfiguratorsRequest,
	fetchConfiguratorsSuccess,
	fetchRatingFailed,
	fetchRatingRequest,
	fetchRatingSuccess,
} from './slice';
import { Bestseller, FetchConfiguratorsParams } from './types';

export function* getConfigurators() {
	try {
		const regionCode = yield* select(selectPageModelRegionId);
		const categoryId = yield* select(selectPageModelCategoryId);

		let bestsellers: Bestseller[] = [];

		const { url, config } = yield* getApiParams(
			'/services-platform/v0/slp/online-offers-by-category-id'
		);
		const params: FetchConfiguratorsParams = {
			regionCode,
			categoryId,
			ignoreAvailability: 'true',
		};

		const data = yield* call(fetchConfigurators, url, params, config);

		const mainServicesIsAvailable = data.configurators?.some(({ isAvailable }) => isAvailable);

		if (!mainServicesIsAvailable) {
			bestsellers = yield* call(fetchConfiguratorSiblings, { categoryId, regionCode });
		}

		if (!mainServicesIsAvailable && !bestsellers?.length) {
			bestsellers = yield* call(fetchBestsellers, { regionCode });
		}

		yield* put(
			fetchConfiguratorsSuccess({
				data,
				bestsellers,
			})
		);
	} catch (error) {
		yield console.log(error);
		yield* put(
			fetchConfiguratorsFailed({
				error: { code: error?.status || 500, message: error?.message || null },
			})
		);
	}
}

export function* getRating() {
	try {
		const regionCode = yield* select(selectPageModelRegionId);
		const configuratorIds = yield* select(selectConfiguratorsIds);

		const { url, config } = yield* getApiParams('/services-platform/v0/slp/ratings:search');
		const params = {
			regionCode,
			configuratorIds,
		};

		const data = yield* call(fetchRating, url, params, config);

		yield* put(
			fetchRatingSuccess({
				data,
			})
		);
	} catch (error) {
		yield* put(
			fetchRatingFailed({
				error: { code: error?.status || 500, message: error?.message || null },
			})
		);
	}
}

export function* fetchConfiguratorSiblings(payload: {
	regionCode: string;
	categoryId: number | string;
}) {
	try {
		const { regionCode, categoryId } = payload;

		const { url, config } = yield* getApiParams(`/sp/configurator/${categoryId}/siblings`);

		return yield* call(fetchConfiguratorSiblingsData, url, { regionCode }, config);
	} catch (error) {
		throw new Error(`Failed to fetch configurator siblings: ${JSON.stringify(error)}`);
	}
}

export function* fetchBestsellers({ regionCode }: { regionCode: string }) {
	try {
		const { url, config } = yield* getApiParams(`/sp/configurators-bestsellers`);

		return yield* call(fetchBestSellersData, url, { regionCode }, config);
	} catch (error) {
		throw new Error(`Failed to fetch bestsellers: ${error}`);
	}
}

export function* configuratorsListingRootSaga() {
	return yield* all([
		takeLatest(fetchConfiguratorsRequest, () => getConfigurators()),
		takeLatest(fetchRatingRequest, () => getRating()),
	]);
}
